export interface HeatmapPoint {
  x: number;
  y: number;
  density: number;
}

export interface HeatmapRenderOpts {
  canvas: HTMLCanvasElement;
  points: HeatmapPoint[];
  radius?: number;
  blur?: number;
}

// Apple-blue → red gradient: cold → cool → warm → hot → critical
const GRADIENT_STOPS: Array<[number, string]> = [
  [0.0, 'rgba(10, 132, 255, 0)'],
  [0.15, 'rgba(10, 132, 255, 0.4)'],
  [0.35, 'rgba(90, 200, 250, 0.6)'],
  [0.55, 'rgba(255, 214, 10, 0.7)'],
  [0.75, 'rgba(255, 159, 10, 0.85)'],
  [1.0, 'rgba(255, 69, 58, 1)'],
];

export function renderHeatmap(opts: HeatmapRenderOpts): void {
  const { canvas, points, radius = 50, blur = 25 } = opts;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const { width, height } = canvas;
  ctx.clearRect(0, 0, width, height);

  if (points.length === 0) return;

  // Step 1: draw alpha-only heatmap (each point as a radial gradient on a temp canvas)
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = height;
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) return;

  const maxDensity = Math.max(...points.map((p) => p.density), 1);

  for (const p of points) {
    const alpha = Math.min(1, p.density / maxDensity);
    const grad = tempCtx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
    grad.addColorStop(0, `rgba(0, 0, 0, ${alpha})`);
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    tempCtx.fillStyle = grad;
    tempCtx.fillRect(p.x - radius, p.y - radius, radius * 2, radius * 2);
  }

  // Step 2: apply gradient mapping based on alpha value
  const imageData = tempCtx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const palette = buildPalette();

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]!; // 0-255
    if (alpha === 0) continue;
    const colorIdx = alpha * 4;
    data[i] = palette[colorIdx]!;
    data[i + 1] = palette[colorIdx + 1]!;
    data[i + 2] = palette[colorIdx + 2]!;
    data[i + 3] = Math.min(255, alpha + 40);
  }

  ctx.putImageData(imageData, 0, 0);

  // Optional blur
  if (blur > 0) {
    ctx.filter = `blur(${blur}px)`;
    ctx.drawImage(canvas, 0, 0);
    ctx.filter = 'none';
  }
}

function buildPalette(): Uint8ClampedArray {
  // Build a 256-step palette by interpolating through GRADIENT_STOPS
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 1;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createLinearGradient(0, 0, 256, 0);
  for (const [stop, color] of GRADIENT_STOPS) grad.addColorStop(stop, color);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 1);
  return ctx.getImageData(0, 0, 256, 1).data;
}
